import type { Key } from "react";
import { useCallback, useMemo, useState } from "react";

import type {
    ITableProps,
    SelectionControllerCallbackPayload,
    SelectionControllerSelectionChangePayload,
} from "@similarweb/pro-logic-components";
import { useTableSelectionController } from "@similarweb/pro-logic-components";

import { MAX_SELECTED_ROWS, ROWS_PER_PAGE } from "../constants/common";

import type { WebsiteSegmentRow } from "./WebsiteSegmentsTableTypes";

type SelectAllPayload = SelectionControllerCallbackPayload<WebsiteSegmentRow>;
type SelectAllCallback = (payload: SelectAllPayload) => void;
type SelectionOnChange = NonNullable<NonNullable<ITableProps<WebsiteSegmentRow>["selection"]>["onChange"]>;

interface UseWebsiteSegmentsTableSelectionOptions {
    sortedData: WebsiteSegmentRow[];
    currentPageRows: WebsiteSegmentRow[];
    currentPage: number;
    hasOwnerActions: boolean;
    translate: (key: string, values?: Record<string, unknown>) => string;
}

interface UseWebsiteSegmentsTableSelectionResult {
    selectedRows: WebsiteSegmentRow[];
    selectedRowKeys: string[];
    selectedRowsCount: number;
    selectedRowsInFilteredTableCount: number;
    clearAllSelectedRows: () => void;
    getCheckboxProps: (record: WebsiteSegmentRow) => { disabled: boolean; title?: string };
    selectionProps: NonNullable<ITableProps<WebsiteSegmentRow>["selection"]>;
    topSelectionDropdownProps: {
        totalItemsCount: number;
        selectedItemsCount: number;
        clearAllLabel: string;
        onSelectAllAcrossPages: () => void;
        onClearAllSelection: () => void;
    };
}

const resolveRowsByKeys = (
    keys: Key[],
    selectableRowsById: Map<string, WebsiteSegmentRow>,
    maxRows: number | undefined,
): WebsiteSegmentRow[] => {
    const resolved: WebsiteSegmentRow[] = [];
    for (const key of keys) {
        const row = selectableRowsById.get(String(key));
        if (!row) {
            continue;
        }
        resolved.push(row);
        if (maxRows !== undefined && resolved.length >= maxRows) {
            break;
        }
    }
    return resolved;
};

const resolveRowKeys = (rows: WebsiteSegmentRow[]): string[] => rows.map(({ id }) => id);

export const useWebsiteSegmentsTableSelection = ({
    sortedData,
    currentPageRows,
    currentPage,
    hasOwnerActions,
    translate,
}: UseWebsiteSegmentsTableSelectionOptions): UseWebsiteSegmentsTableSelectionResult => {
    const [selectedRows, setSelectedRows] = useState<WebsiteSegmentRow[]>([]);

    const selectedRowKeys = useMemo<string[]>(() => selectedRows.map(({ id }) => id), [selectedRows]);
    const selectedRowsCount = selectedRows.length;

    const maxSelectableRows = hasOwnerActions ? undefined : MAX_SELECTED_ROWS;

    const selectableRowsById = useMemo(() => {
        const rowsMap = new Map<string, WebsiteSegmentRow>();
        sortedData.forEach((row) => {
            if (!row.isBlacklisted) {
                rowsMap.set(row.id, row);
            }
        });
        return rowsMap;
    }, [sortedData]);

    const selectableRowsAcrossPages = useMemo(() => sortedData.filter((row) => !row.isBlacklisted), [sortedData]);

    const selectedRowKeysSet = useMemo(() => new Set(selectedRowKeys), [selectedRowKeys]);

    const selectedRowsInFilteredTableCount = useMemo(
        () => selectableRowsAcrossPages.reduce((count, row) => (selectedRowKeysSet.has(row.id) ? count + 1 : count), 0),
        [selectableRowsAcrossPages, selectedRowKeysSet],
    );

    const handleControllerSelectionChange = useCallback(
        (payload: SelectionControllerSelectionChangePayload<WebsiteSegmentRow>) => {
            const { selectedRowKeys: nextKeys, info } = payload;
            const nextRows =
                info?.type === "all" && nextKeys.length === 0
                    ? []
                    : resolveRowsByKeys(nextKeys, selectableRowsById, maxSelectableRows);

            payload.setSelectedRowKeys(resolveRowKeys(nextRows));
            setSelectedRows(nextRows);
        },
        [selectableRowsById, maxSelectableRows],
    );

    const handleControllerSelectAllAcrossPages = useCallback<SelectAllCallback>(
        (payload) => {
            if (!selectableRowsAcrossPages.length) {
                return;
            }
            const capped = maxSelectableRows
                ? selectableRowsAcrossPages.slice(0, maxSelectableRows)
                : selectableRowsAcrossPages;
            setSelectedRows(capped);
            payload.setSelectedRowKeys(capped.map(({ id }) => id));
        },
        [selectableRowsAcrossPages, maxSelectableRows],
    );

    const handleControllerClearAll = useCallback<SelectAllCallback>((payload) => {
        payload.defaultAction();
        setSelectedRows([]);
    }, []);

    const controller = useTableSelectionController<WebsiteSegmentRow>({
        allRows: selectableRowsAcrossPages,
        pageRows: currentPageRows,
        rowKey: "id",
        page: currentPage,
        pageSize: ROWS_PER_PAGE,
        initialSelectedRowKeys: [],
        onSelectionChange: handleControllerSelectionChange,
        onSelectAllAcrossPages: handleControllerSelectAllAcrossPages,
        onClearAllSelection: handleControllerClearAll,
    });

    const clearAllSelectedRows = useCallback(() => {
        controller.actions.setSelectedRowKeys([]);
        setSelectedRows([]);
    }, [controller.actions]);

    const getCheckboxProps = useCallback(
        (record: WebsiteSegmentRow) => {
            const isRowSelected = selectedRowKeysSet.has(record.id);
            const isMaxLimitExceeded =
                maxSelectableRows !== undefined && selectedRowKeys.length >= maxSelectableRows && !isRowSelected;
            const isBlacklisted = !!record.isBlacklisted;
            const isDisabled = isBlacklisted || isMaxLimitExceeded;
            const disabledTooltipKey = isBlacklisted
                ? "segment.analysis.org.table.column.disabled.tooltip"
                : "segment.analysis.homepage.table.row.selection.disabled";

            return {
                disabled: isDisabled,
                title: isDisabled ? translate(disabledTooltipKey, { limit: MAX_SELECTED_ROWS }) : undefined,
            };
        },
        [selectedRowKeysSet, selectedRowKeys.length, maxSelectableRows, translate],
    );

    const selectionProps = useMemo<NonNullable<ITableProps<WebsiteSegmentRow>["selection"]>>(
        () => ({
            enabled: true,
            columnWidth: 48,
            preserveSelectedRowKeys: true,
            selectedRowKeys,
            onChange: controller.tableSelection.onChange as SelectionOnChange,
            getCheckboxProps,
        }),
        [selectedRowKeys, controller.tableSelection.onChange, getCheckboxProps],
    );

    const topSelectionDropdownProps = useMemo(
        () => ({
            totalItemsCount: selectableRowsAcrossPages.length,
            selectedItemsCount: selectedRowsInFilteredTableCount,
            clearAllLabel: translate("website_segments.segments_table.clear_all.label"),
            onSelectAllAcrossPages: controller.actions.selectAllAcrossPages,
            onClearAllSelection: controller.actions.clearAllSelection,
        }),
        [
            selectableRowsAcrossPages.length,
            selectedRowsInFilteredTableCount,
            translate,
            controller.actions.selectAllAcrossPages,
            controller.actions.clearAllSelection,
        ],
    );

    return {
        selectedRows,
        selectedRowKeys,
        selectedRowsCount,
        selectedRowsInFilteredTableCount,
        clearAllSelectedRows,
        getCheckboxProps,
        selectionProps,
        topSelectionDropdownProps,
    };
};
